/**
 * The polling loop behind live updates (no React): asks `events/?after=<cursor>` every few seconds,
 * hands new events on, and recovers from errors, sleep and lost cursors.
 *
 *  - Every 4 s while someone looks at the company (this tab, or another tab that says so), every
 *    30 s otherwise; at once on focus, on coming back online and when the server says `has_more`.
 *  - Errors back off exponentially (2 s, 4 s, … 30 s) with jitter, so a restarting server isn't
 *    hammered by every open tab at the same moment.
 *  - The cursor is kept in memory and in sessionStorage per company, so a reload continues from it.
 *  - `reset` from the server (cursor unknown or purged), or a long gap without a successful poll
 *    (a sleeping laptop), calls `onReset`: the app refetches everything. The very first poll of a
 *    fresh tab only learns the cursor (the page has just loaded everything anyway).
 *
 * Dedupe by id is the caller's job (the server repeats recent events below the cursor on purpose).
 */
import type { LiveEvent } from "./topics";

export type LiveStatus = "connecting" | "live" | "reconnecting" | "offline";
export type FeedPage = { events: LiveEvent[]; cursor: number; reset: boolean; has_more: boolean };

export const VISIBLE_MS = 4_000;
export const HIDDEN_MS = 30_000;
export const MAX_BACKOFF_MS = 30_000;
export const GAP_MS = 10 * 60_000;
const MIN_POKE_MS = 1_500;

export type FeedOptions = {
  fetchPage: (after: number | null) => Promise<FeedPage>;
  onEvents: (events: LiveEvent[]) => void;
  onReset: () => void;
  onStatus: (status: LiveStatus) => void;
  /** Someone is looking (this tab visible, or a listening tab reported it lately). */
  isActive: () => boolean;
  isOnline?: () => boolean;
  storageKey?: string;
  random?: () => number;
  now?: () => number;
};

export class LiveFeed {
  private cursor: number | null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private inFlight = false;
  private failures = 0;
  private lastSuccess: number | null = null;
  private lastAttempt = 0;
  private status: LiveStatus | null = null;
  private readonly opts: Required<Omit<FeedOptions, "storageKey">> & { storageKey?: string };

  constructor(options: FeedOptions) {
    this.opts = {
      isOnline: () => (typeof navigator === "undefined" ? true : navigator.onLine !== false),
      random: Math.random,
      now: Date.now,
      ...options,
    };
    this.cursor = this.readCursor();
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.setStatus(this.opts.isOnline() ? "connecting" : "offline");
    this.schedule(0);
  }

  stop() {
    this.running = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  /** Poll now (focus, online, another tab asking), unless a poll is running or ran less than
   * `minAgeMs` ago. `keepBackoff` leaves a failing feed on its backoff (heartbeats from other tabs);
   * focus and coming back online try at once. */
  poke(minAgeMs = MIN_POKE_MS, keepBackoff = false) {
    if (!this.running || this.inFlight) return;
    if (keepBackoff && this.failures > 0) return;
    if (this.opts.now() - this.lastAttempt < minAgeMs) return;
    this.schedule(0);
  }

  get currentCursor() {
    return this.cursor;
  }

  private schedule(delay: number) {
    if (!this.running) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.tick(), delay);
  }

  private nextDelay() {
    if (this.failures > 0) {
      const base = Math.min(MAX_BACKOFF_MS, 2_000 * 2 ** (this.failures - 1));
      return Math.round(base * (0.5 + this.opts.random() * 0.5));
    }
    return this.opts.isActive() ? VISIBLE_MS : HIDDEN_MS;
  }

  private async tick() {
    this.timer = null;
    if (!this.running || this.inFlight) return;
    if (!this.opts.isOnline()) {
      this.setStatus("offline");
      this.schedule(HIDDEN_MS); // "online" pokes sooner
      return;
    }
    this.inFlight = true;
    this.lastAttempt = this.opts.now();
    let again = false;
    try {
      const hadCursor = this.cursor !== null;
      const page = await this.opts.fetchPage(this.cursor);
      if (!this.running) return;
      const now = this.opts.now();
      const slept = this.lastSuccess !== null && now - this.lastSuccess > GAP_MS;
      this.lastSuccess = now;
      this.failures = 0;
      this.writeCursor(page.cursor);
      if ((page.reset && hadCursor) || slept) this.opts.onReset();
      else if (page.events.length) this.opts.onEvents(page.events);
      this.setStatus("live");
      again = page.has_more;
    } catch {
      if (!this.running) return;
      this.failures += 1;
      this.setStatus(this.opts.isOnline() ? "reconnecting" : "offline");
    } finally {
      this.inFlight = false;
    }
    this.schedule(again ? 0 : this.nextDelay());
  }

  private setStatus(status: LiveStatus) {
    if (status === this.status) return;
    this.status = status;
    this.opts.onStatus(status);
  }

  private readCursor(): number | null {
    if (!this.opts.storageKey) return null;
    try {
      const raw = sessionStorage.getItem(this.opts.storageKey);
      const value = raw === null ? NaN : Number(raw);
      return Number.isInteger(value) && value >= 0 ? value : null;
    } catch {
      return null;
    }
  }

  private writeCursor(cursor: number) {
    this.cursor = cursor;
    if (!this.opts.storageKey) return;
    try {
      sessionStorage.setItem(this.opts.storageKey, String(cursor));
    } catch {
      // Private mode or full storage: the in-memory cursor is enough.
    }
  }
}

/** Remembers the last few thousand event ids, so repeats (the overlap window, other tabs) count once. */
export class SeenIds {
  private readonly ids = new Set<number>();
  private readonly order: number[] = [];
  constructor(private readonly limit = 2_000) {}

  /** The events not seen before, now remembered. */
  fresh<T extends { id: number }>(events: T[]): T[] {
    const out: T[] = [];
    for (const event of events) {
      if (this.ids.has(event.id)) continue;
      this.ids.add(event.id);
      this.order.push(event.id);
      out.push(event);
    }
    while (this.order.length > this.limit) this.ids.delete(this.order.shift()!);
    return out;
  }
}
